<template>
  <v-container id="technical-review-form" class="position-relative">
    <app-city-header />

    <v-overlay :value="actionLoading" absolute>
      <v-progress-circular indeterminate color="primary" size="64" />
    </v-overlay>

    <v-form ref="formRef">
      <v-row class="mt-4">
        <v-col cols="12" md="12">
          <v-select
            v-model="selectedCriterion"
            :items="criteria"
            item-text="displayLabel"
            item-value="value"
            item-disabled="disabled"
            label="Critério"
            :disabled="isEdit"
            :rules="[rules.required]"
          >
            <template v-slot:item="{ item }">
              <span class="criterion-option-label">{{ item.displayLabel }}</span>
            </template>
            <template v-slot:selection="{ item, index }">
              <span v-if="item && index === 0">{{ item.displayLabel }}</span>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-alert v-if="contextoNacional && contextoNacional.habilitado" type="info" outlined>
        {{ contextoNacional.fase === 'REVISAO_TECNICA_UF' ? 'Revisão solicitada pela UF. Critérios nacionais ou com decisão Nacional anterior seguem ao Nacional; os demais são decididos pela UF.' : 'Revisão técnica Nacional. A recomendação será aplicada ao deferir a revisão.' }}
      </v-alert>
      <v-alert v-if="contextoNacional && contextoNacional.fase === 'REVISAO_TECNICA_UF' && criterioSelecionado && criterioSelecionado.jaRevisadoNacional" color="blue lighten-5" class="national-routed" prominent>
        Este critério já foi revisto pelo Nacional. Sua solicitação será encaminhada ao Nacional para julgamento. A decisão anterior permanece válida; a UF não pode substituí-la diretamente.
      </v-alert>
      <v-row v-if="contextoNacional && contextoNacional.habilitado && criterioSelecionado">
        <v-col cols="12" md="6">
          <v-select v-if="criterioSelecionado.tipoRecomendacao === 'FAIXA'" v-model="form.faixaRecomendadaId" :items="criterioSelecionado.faixas" item-value="id" item-text="rotulo" label="Pontuação recomendada" :rules="[rules.required]" />
          <v-select v-else-if="criterioSelecionado.tipoRecomendacao === 'FIXO'" v-model="form.resultadoRecomendado" :items="['APROVADO', 'REJEITADO']" label="Resultado recomendado" :rules="[rules.required]" />
          <v-alert v-else-if="criterioSelecionado.tipoRecomendacao === 'SIM_NAO'" type="info" outlined>
            Resposta atual: {{ respostaTexto(criterioSelecionado.respostaOriginal) }}. Ao deferir: {{ respostaTexto(criterioSelecionado.respostaOriginal) === 'SIM' ? 'NÃO' : 'SIM' }}.
          </v-alert>
          <v-alert v-else type="info" outlined>Revisão documental: o parecer e os anexos seráo registrados. Arquivos e áudios não possuem inversão SIM/NÃO.</v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="form.justificativa"
            label="Justificativa detalhada"
            auto-grow
            counter="10000"
            :rules="[rules.required, rules.minJustificativa, rules.maxJustificativa]"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <h3 class="subtitle-1 font-weight-medium">Anexos enviados</h3>
          <div v-if="existingAttachments.length">
            <v-chip
              v-for="anexo in existingAttachments"
              :key="anexo.id"
              class="ma-1"
              close
              outlined
              @click:close="removeExistingAttachment(anexo)"
            >
              {{ anexo.nome }}
            </v-chip>
          </div>
          <div v-else class="grey--text">Nenhum anexo cadastrado.</div>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="form.arquivos"
            label="Adicionar anexos"
            multiple
            show-size
            truncate-length="25"
            :rules="[rules.fileSize]"
          />
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12" class="text-right">
          <v-btn text color="secondary" class="mr-2" @click="voltar">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            class="mr-2"
            :loading="saving"
            @click="salvar(false)"
            :disabled="!isEditable || actionLoading"
          >
            Salvar rascunho
          </v-btn>
          <v-btn
            color="success"
            :loading="sending"
            @click="salvar(true)"
            :disabled="!isEditable || actionLoading"
          >
            Enviar para análise
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { AppCityHeader } from "@/modules/city";
import { technicalReviewAPI } from "../API/TechnicalReviewAPI";
import { buildCriterionDisplayLabel } from "@/helpers/criteria";
import { findStatusMeta } from "../constants";

export default {
  name: "TechnicalReviewForm",
  components: { AppCityHeader },
  data() {
    return {
      contextoNacional: null,
      fluxoNacional: null,
      loading: false,
      saving: false,
      sending: false,
      criteria: [],
      selectedCriterion: null,
      form: {
        seloRevisaoTecnicaId: null,
        municipioId: this.$route.params.municipioId,
        perguntaId: null,
        perguntaMunicipioId: null,
        ehPerguntaClienteOculto: false,
        notaOriginal: "",
        faixaRecomendadaId: null,
        resultadoRecomendado: null,
        revisaoAnteriorId: null,
        versaoEsperada: null,
        notaPleiteada: "",
        justificativa: "",
        arquivos: [],
      },
      existingAttachments: [],
      attachmentsToRemove: [],
      currentStatus: "RASCUNHO",
      config: JSON.parse(localStorage.getItem("config")) || {},
      groupsConfig: JSON.parse(localStorage.getItem("groupsConfig")) || {},
      actionLoading: false,
      rules: {
        required: (value) => !!value || "Campo obrigatório",
        minJustificativa: (value) => !this.contextoNacional?.habilitado || String(value || '').trim().length >= 10 || 'Informe justificativa com pelo menos 10 caracteres.',
        maxJustificativa: (value) =>
          !value || value.length <= 10000 || "Máximo de 10000 caracteres",
        fileSize: () => true,
      },
    };
  },
  computed: {
    ...mapGetters(["isFullAdmin", "cityId"]),
    routeReviewId() {
      const rawId = this.$route.params.revisaoId;
      if (!rawId) {
        return null;
      }
      const parsedId = Number(rawId);
      return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
    },
    isEdit() {
      return this.routeReviewId !== null;
    },
    municipioId() {
      return this.$route.params.municipioId || this.cityId;
    },
    criterioSelecionado() { return this.criteria.find((item) => item.value === this.selectedCriterion); },
    canCreate() {
      if (this.contextoNacional && this.contextoNacional.habilitado) return this.contextoNacional.podeCriar;
      if (this.isFullAdmin) {
        return true;
      }

      const podeAcessarRevisoes =
        (this.config.revisaoTecnica ?? false) &&
        (this.groupsConfig.revisaoTecnica ?? false);

      const podeCriarRevisoes =
        (this.config.criarRevisaoTecnica ?? false) &&
        (this.groupsConfig.criarRevisaoTecnica ?? false);

      return podeAcessarRevisoes && podeCriarRevisoes;
    },
    canManage() {
      return (
        this.isFullAdmin ||
        ((this.config.revisaoTecnica ?? false) &&
          (this.groupsConfig.revisaoTecnica ?? false))
      );
    },
    isEditable() {
      if (this.fluxoNacional) return this.fluxoNacional.podeEditar;
      if (!this.canManage) {
        return false;
      }

      if (!this.isEdit && !this.canCreate) {
        return false;
      }
      return ["RASCUNHO", "COMPLEMENTAR"].includes(this.currentStatus);
    },
  },
  async created() {
    try {
      const { data } = await technicalReviewAPI.context(this.municipioId);
      this.contextoNacional = this.normalizar(data);
      if (!this.isEdit && !this.canCreate) { this.notifyError("Criação não liberada para este perfil nesta etapa."); this.voltar(); return; }
      await this.carregarCriterios();
      if (this.isEdit) await this.carregarRevisaoTecnica();
      else if (this.$route.query.anterior) {
        const { data: anterior } = await technicalReviewAPI.get(this.$route.query.anterior);
        const cab = this.normalizar(anterior).cabecalho;
        this.form.revisaoAnteriorId = cab.seloRevisaoTecnicaId;
        this.form.perguntaId = cab.perguntaId; this.form.ehPerguntaClienteOculto = cab.ehPerguntaClienteOculto;
        this.syncSelectedCriterion();
        if (cab.fluxoNacional) { this.form.faixaRecomendadaId = cab.fluxoNacional.faixaRecomendadaId; this.form.resultadoRecomendado = cab.fluxoNacional.resultadoRecomendado; }
      }
    } catch (error) { this.notifyError(error.response?.data?.message || error.response?.data || "Não foi possível abrir a revisão."); }
  },
  watch: {
    selectedCriterion(newValue) {
      if (!newValue) {
        this.form.perguntaId = null;
        this.form.perguntaMunicipioId = null;
        this.form.ehPerguntaClienteOculto = false;
        this.form.notaOriginal = "";
        this.form.notaPleiteada = "";
        return;
      }

      const criterio = this.criteria.find(
        (item) => item.value === newValue
      );
      if (criterio) {
        const perguntaId = this.normalizeIntegerId(criterio.perguntaId);
        this.form.perguntaId = perguntaId ?? criterio.perguntaId ?? null;
        this.form.perguntaMunicipioId =
          this.normalizeIntegerId(criterio.perguntaMunicipioId) ??
          criterio.perguntaMunicipioId ??
          null;
        const ehClienteOculto = this.normalizeClienteOcultoFlag(
          criterio.ehPerguntaClienteOculto
        );
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
        const nota = Number(criterio.notaOriginal ?? 0);
        this.form.notaOriginal = Number.isFinite(nota)
          ? nota.toFixed(2)
          : "0.00";
        this.form.notaPleiteada = this.form.notaOriginal;
      }
    },
  },
  methods: {
    normalizar(value) { if (Array.isArray(value)) return value.map(this.normalizar); if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k,v]) => [k.charAt(0).toLowerCase()+k.slice(1), this.normalizar(v)])); return value; },
    respostaTexto(value) { return ['true', 'sim', '1'].includes(String(value).toLowerCase()) ? 'SIM' : ['false', 'não', 'nao', '0'].includes(String(value).toLowerCase()) ? 'NÃO' : 'Sem resposta'; },
    buildCriterionValue(perguntaId, ehPerguntaClienteOculto, perguntaMunicipioId) {
      const normalizedPerguntaId = this.normalizeIntegerId(perguntaId);
      const normalizedPerguntaMunicipioId = this.normalizeIntegerId(
        perguntaMunicipioId
      );
      const id = normalizedPerguntaId ?? perguntaId ?? "";
      const municipioId =
        normalizedPerguntaMunicipioId ??
        normalizedPerguntaId ??
        perguntaMunicipioId ??
        perguntaId ??
        "";
      const prefix = ehPerguntaClienteOculto ? "clienteoculto" : "pergunta";
      return `${prefix}-${municipioId}-${id}`;
    },
    normalizeClienteOcultoFlag(value) {
      if (value === true || value === 1) {
        return true;
      }

      if (value === false || value === 0) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "sim", "yes"].includes(normalized)) {
          return true;
        }
        if (["0", "false", "nao", "não", "no"].includes(normalized)) {
          return false;
        }
      }

      return Boolean(value);
    },
    syncSelectedCriterion() {
      if (!this.form.perguntaId || !this.criteria.length) {
        return;
      }

      const formPerguntaId = this.normalizeIntegerId(this.form.perguntaId);
      const ehClienteOculto = this.normalizeClienteOcultoFlag(
        this.form.ehPerguntaClienteOculto
      );

      const criterio = this.criteria.find((item) => {
        const itemPerguntaId = this.normalizeIntegerId(item.perguntaId);
        const itemEhClienteOculto = this.normalizeClienteOcultoFlag(
          item.ehPerguntaClienteOculto
        );
        return (
          (itemPerguntaId ?? item.perguntaId) ===
            (formPerguntaId ?? this.form.perguntaId) &&
          itemEhClienteOculto === ehClienteOculto
        );
      });

      if (criterio) {
        this.form.perguntaMunicipioId =
          this.normalizeIntegerId(criterio.perguntaMunicipioId) ??
          criterio.perguntaMunicipioId ??
          null;
        this.selectedCriterion = criterio.value;
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
      }
    },
    notifySuccess(message) {
      if (this.$toast?.success) {
        this.$toast.success(message);
      } else if (this.$notification?.success) {
        this.$notification.success(message);
      } else {
        console.info(message);
      }
    },
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      } else if (this.$notification?.error) {
        this.$notification.error(message);
      } else {
        console.error(message);
      }
    },
    async carregarCriterios() {
      try {
        const { data } = await technicalReviewAPI.criteria(this.municipioId);
        const lista = Array.isArray(data) ? data : [];
        this.criteria = lista.map((item) => {
          const perguntaIdRaw = item.PerguntaId ?? item.perguntaId ?? null;
          const perguntaMunicipioIdRaw =
            item.PerguntaMunicipioId ?? item.perguntaMunicipioId ?? null;
          const perguntaId = this.normalizeIntegerId(perguntaIdRaw);
          const perguntaMunicipioId = this.normalizeIntegerId(
            perguntaMunicipioIdRaw
          );
          const texto = item.PerguntaTitulo ?? item.perguntaTitulo;
          const grupoNome = item.GrupoNome ?? item.grupoNome;
          const rawLabel = grupoNome ? `${grupoNome} - ${texto}` : texto;
          const ehPerguntaClienteOculto = this.normalizeClienteOcultoFlag(
            item.EhPerguntaClienteOculto ?? item.ehPerguntaClienteOculto ?? false
          );
          const displayLabel = ehPerguntaClienteOculto
            ? rawLabel
            : buildCriterionDisplayLabel(rawLabel);
          const notaOriginal = Number(
            item.NotaOriginal ?? item.notaOriginal ?? 0
          ).toFixed(2);
          const bloqueado =
            item.PossuiRevisaoTecnicaEmAberto ??
            item.possuiRevisaoTecnicaEmAberto ??
            item.PossuiRecursoEmAberto ??
            item.possuiRecursoEmAberto;
          const value = this.buildCriterionValue(
            perguntaId ?? perguntaIdRaw,
            ehPerguntaClienteOculto,
            perguntaMunicipioId ?? perguntaMunicipioIdRaw
          );
          return {
            value,
            perguntaId: perguntaId ?? perguntaIdRaw ?? null,
            perguntaMunicipioId:
              perguntaMunicipioId ?? perguntaMunicipioIdRaw ?? null,
            label: rawLabel,
            displayLabel,
            grupoNome,
            notaOriginal,
            disabled: !!bloqueado || item.Disponivel === false || item.disponivel === false,
            tipoRecomendacao: item.TipoRecomendacao ?? item.tipoRecomendacao,
            jaRevisadoNacional: item.JaRevisadoNacional ?? item.jaRevisadoNacional ?? false,
            respostaOriginal: item.RespostaOriginal ?? item.respostaOriginal,
            faixas: this.normalizar(item.Faixas ?? item.faixas ?? []).map((f) => ({...f, rotulo: `${f.nome} (${f.pontos} pontos)`})),
            ehPerguntaClienteOculto,
          };
        });

        this.syncSelectedCriterion();
      } catch (error) {
        console.error("Erro ao carregar critérios", error);
        this.notifyError("Não foi possível carregar os critérios elegíveis");
      }
    },
    async carregarRevisaoTecnica() {
      this.loading = true;
      try {
        const revisaoId = this.routeReviewId;
        if (!revisaoId) {
          this.notifyError("Revisão técnica inválida");
          this.$router.replace(
            `/cidade/${this.municipioId}/revisoes-tecnicas`
          );
          return;
        }

        const { data } = await technicalReviewAPI.get(revisaoId);
        const cabecalho = data.Cabecalho || data.cabecalho;
        if (!cabecalho) {
          throw new Error("Dados da revisão técnica inválidos");
        }

        const statusMeta = findStatusMeta(cabecalho.Status || cabecalho.status);
        this.currentStatus = statusMeta.value;

        if (!this.isEditable) {
          this.$router.replace(
            `/cidade/${this.municipioId}/revisoes-tecnicas/${cabecalho.SeloRevisaoTecnicaId}`
          );
          return;
        }

        this.fluxoNacional = this.normalizar(cabecalho.FluxoNacional ?? cabecalho.fluxoNacional);
        if (this.fluxoNacional) { this.form.versaoEsperada = this.fluxoNacional.versao; this.form.faixaRecomendadaId = this.fluxoNacional.faixaRecomendadaId; this.form.resultadoRecomendado = this.fluxoNacional.resultadoRecomendado; }
        this.form.seloRevisaoTecnicaId = cabecalho.SeloRevisaoTecnicaId;
        const municipioId =
          this.normalizeIntegerId(
            cabecalho.MunicipioId ?? cabecalho.municipioId ?? null
          ) ?? (cabecalho.MunicipioId ?? cabecalho.municipioId ?? null);
        this.form.municipioId = municipioId;
        const perguntaId = this.normalizeIntegerId(
          cabecalho.PerguntaId ?? cabecalho.perguntaId ?? null
        );
        this.form.perguntaId = perguntaId ?? cabecalho.PerguntaId ?? cabecalho.perguntaId;
        this.form.perguntaMunicipioId = null;
        const ehClienteOculto = this.normalizeClienteOcultoFlag(
          cabecalho.EhPerguntaClienteOculto ??
            cabecalho.ehPerguntaClienteOculto ??
            false
        );
        this.form.ehPerguntaClienteOculto = ehClienteOculto;
        this.syncSelectedCriterion();
        this.form.notaOriginal = Number(
          cabecalho.NotaOriginal ?? cabecalho.notaOriginal ?? 0
        ).toFixed(2);
        const notaPleiteada = Number(
          cabecalho.NotaPleiteada ?? cabecalho.notaPleiteada ?? this.form.notaOriginal
        );
        this.form.notaPleiteada = Number.isFinite(notaPleiteada)
          ? notaPleiteada.toFixed(2)
          : this.form.notaOriginal;
        this.form.justificativa = data.Justificativa || data.justificativa || "";

        const anexos = data.Anexos || data.anexos || [];
        this.existingAttachments = anexos.map((item) => ({
          id:
            item.SeloRevisaoTecnicaAnexoId ??
            item.seloRevisaoTecnicaAnexoId ??
            item.SeloRecursoAnexoId ??
            item.seloRecursoAnexoId,
          nome: item.Nome ?? item.nome,
        }));
      } catch (error) {
        console.error("Erro ao carregar revisão técnica", error);
        this.notifyError("Não foi possível carregar a revisão técnica");
        this.$router.replace(`/cidade/${this.municipioId}/revisoes-tecnicas`);
      } finally {
        this.loading = false;
      }
    },
    removeExistingAttachment(anexo) {
      this.attachmentsToRemove.push(anexo.id);
      this.existingAttachments = this.existingAttachments.filter(
        (item) => item.id !== anexo.id
      );
    },
    async salvar(sendAfterSave) {
      if (!this.isEditable) {
        this.notifyError("Esta revisão técnica não pode mais ser editada");
        return;
      }

      if (!this.$refs.formRef.validate()) {
        return;
      }

      if (sendAfterSave && !this.validarEnvio()) {
        return;
      }

      if (this.actionLoading) {
        return;
      }

      this.saving = !sendAfterSave;
      this.sending = sendAfterSave;

      this.actionLoading = true;

      try {
        const revisaoIdFromState =
          this.form.seloRevisaoTecnicaId !== null &&
          this.form.seloRevisaoTecnicaId !== undefined
            ? Number(this.form.seloRevisaoTecnicaId)
            : null;
        const revisaoId =
          revisaoIdFromState && !Number.isNaN(revisaoIdFromState)
            ? revisaoIdFromState
            : this.routeReviewId;

        if (this.isEdit && (!revisaoId || Number.isNaN(revisaoId))) {
          this.notifyError("Revisão técnica inválida");
          return;
        }

        const notaPleiteadaValor =
          this.form.notaPleiteada !== ""
            ? this.form.notaPleiteada
            : this.form.notaOriginal || 0;

        const perguntaId = this.normalizeIntegerId(this.form.perguntaId);
        if (!perguntaId) {
          this.notifyError(
            "Selecione um critério válido antes de salvar a revisão técnica"
          );
          return;
        }

        const municipioId =
          this.normalizeIntegerId(this.form.municipioId) ??
          Number(this.form.municipioId);

        const payload = {
          SeloRevisaoTecnicaId: revisaoId || 0,
          MunicipioId: municipioId || 0,
          PerguntaId: perguntaId,
          EhPerguntaClienteOculto: this.normalizeClienteOcultoFlag(
            this.form.ehPerguntaClienteOculto
          ),
          NotaOriginal: this.normalizeDecimalForApi(this.form.notaOriginal),
          NotaPleiteada: this.normalizeDecimalForApi(notaPleiteadaValor),
          Justificativa: this.form.justificativa,
          FaixaRecomendadaId: this.form.faixaRecomendadaId,
          ResultadoRecomendado: this.form.resultadoRecomendado,
          RevisaoAnteriorId: this.form.revisaoAnteriorId,
          VersaoEsperada: this.form.versaoEsperada,
          AnexosRemovidos: this.attachmentsToRemove.join(","),
          arquivos: this.form.arquivos || [],
        };

        let response;
        let finalId = revisaoId || 0;

        if (this.isEdit) {
          response = await technicalReviewAPI.update(finalId, payload);
        } else {
          response = await technicalReviewAPI.create(payload);
          finalId = Number(
            response?.data?.SeloRevisaoTecnicaId ??
              response?.data?.seloRevisaoTecnicaId ??
              0
          );
          this.form.seloRevisaoTecnicaId = finalId || null;
        }

        if (sendAfterSave) {
          if (!finalId) {
            this.notifyError(
              "Não foi possível identificar a revisão técnica para envio"
            );
            return;
          }

          await technicalReviewAPI.send(finalId, { VersaoEsperada: response?.data?.VersaoEsperada ?? response?.data?.versaoEsperada ?? this.form.versaoEsperada });
        }

        this.form.seloRevisaoTecnicaId = finalId;

        this.notifySuccess(
          sendAfterSave
            ? "Revisão técnica enviada para análise com sucesso"
            : "Revisão técnica salva com sucesso"
        );

        this.$router.push(
          `/cidade/${this.municipioId}/revisoes-tecnicas/${this.form.seloRevisaoTecnicaId}`
        );
      } catch (error) {
        console.error("Erro ao salvar revisão técnica", error);
        const status = error?.response?.status;
        const data = error?.response?.data;
        const message = typeof data === 'string' ? data : data?.message || data?.Message;
        this.notifyError([400, 401, 403, 409].includes(status) && message ? message : "Não foi possível salvar a revisão técnica");
      } finally {
        this.saving = false;
        this.sending = false;
        this.actionLoading = false;
      }
    },
    validarEnvio() {
      if (!this.form.justificativa) {
        this.notifyError("Informe a justificativa antes de enviar");
        return false;
      }

      return true;
    },
    normalizeIntegerId(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "number" && Number.isInteger(value) && value > 0) {
        return value;
      }

      const numeric = Number(value);
      if (Number.isInteger(numeric) && numeric > 0) {
        return numeric;
      }

      const parsed = Number.parseInt(value, 10);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    normalizeDecimalForApi(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }

      const numericValue = Number(
        typeof value === "string" ? value.replace(",", ".") : value
      );

      if (!Number.isFinite(numericValue)) {
        return "0";
      }

      return numericValue.toFixed(2).replace(".", ",");
    },
    voltar() {
      this.$router.push(`/cidade/${this.municipioId}/revisoes-tecnicas`);
    },
  },
};
</script>

<style scoped>
#technical-review-form {
  min-height: 80vh;
}

.position-relative {
  position: relative;
}

.criterion-option-label {
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.4;
  padding: 8px 0;
}
</style>

